import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface ToastOptions {
  actionLabel?: string
  onAction?: () => void
}

interface Toast {
  id: number
  message: string
  variant: ToastVariant
  actionLabel?: string
  onAction?: () => void
}

interface ToastContextValue {
  show: (message: string, variant?: ToastVariant, opts?: ToastOptions) => void
  success: (message: string, opts?: ToastOptions) => void
  error: (message: string, opts?: ToastOptions) => void
  info: (message: string, opts?: ToastOptions) => void
  warning: (message: string, opts?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 1

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const show = useCallback((message: string, variant: ToastVariant = 'info', opts?: ToastOptions) => {
    const id = nextId++
    setToasts(prev => [...prev, { id, message, variant, actionLabel: opts?.actionLabel, onAction: opts?.onAction }])
    // Auto-dismiss after 5s (errors live longer; toasts with actions stick around longer too)
    const ttl = variant === 'error' ? 8000 : opts?.actionLabel ? 8000 : 5000
    setTimeout(() => remove(id), ttl)
  }, [remove])

  const value: ToastContextValue = {
    show,
    success: (m, opts) => show(m, 'success', opts),
    error: (m, opts) => show(m, 'error', opts),
    info: (m, opts) => show(m, 'info', opts),
    warning: (m, opts) => show(m, 'warning', opts),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none"
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: '✓' },
  error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: '✕' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'ⓘ' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: '⚠' },
}

const ToastItem: React.FC<{ toast: Toast; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    // animate in on mount
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])
  const styles = VARIANT_STYLES[toast.variant]
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-start gap-3 rounded-xl border ${styles.bg} ${styles.border} ${styles.text} px-4 py-3 shadow-lg transition-all duration-200 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
      }`}
    >
      <span aria-hidden="true" className="text-lg leading-none mt-0.5">{styles.icon}</span>
      <p className="text-sm font-medium flex-1 leading-snug">{toast.message}</p>
      {toast.actionLabel && toast.onAction && (
        <button
          type="button"
          onClick={() => { toast.onAction!(); onDismiss() }}
          className="text-sm font-semibold underline hover:no-underline whitespace-nowrap"
        >
          {toast.actionLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-slate-400 hover:text-slate-600 leading-none text-lg"
      >
        ×
      </button>
    </div>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback to alert when provider is missing — guarantees no silent failures
    return {
      show: (m) => window.alert(m),
      success: (m) => window.alert(m),
      error: (m) => window.alert(m),
      info: (m) => window.alert(m),
      warning: (m) => window.alert(m),
    }
  }
  return ctx
}
